import React, { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, FolderOpen, Users, Trash2, PencilLine, RefreshCw } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import EditProjectModal from "@/components/projects/EditProjectModal";
import type { ProjectPayload } from "@shared/interfaces/projectPayload.interface";

interface ProjectData {
  id: string;
  name: string;
  description: string;
  owner_id: number;
  owner_email: string;
  owner_name: string;
  client_org: string;
  created_at: string;
  updated_at: string;
  status: string;
  module_completion: {
    maturity: number;
    roi: number;
    value_engineering: number;
  };
  completion?: {
    maturity: number;
    roi: number;
    value_engineering: number;
  };
  collaborators: string[];
  salesforce_opportunity_id?: string | null;
  salesforce_task_id?: string | null;
}

// ProjectStatusBadge component
function ProjectStatusBadge({ completed }: { completed: boolean }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
        completed 
          ? 'bg-green-900/30 text-green-400 border border-green-500/30' 
          : 'bg-gray-900/30 text-gray-400 border border-gray-500/30'
      }`}
    >
      {completed ? '✓' : '○'}
    </span>
  );
}

// ParticipantsModal component
function ParticipantsModal({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const [participants, setParticipants] = useState<any[]>([]);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'Collaborator' });

  useEffect(() => {
    apiRequest(`/api/projects/${projectId}/participants`).then(setParticipants).catch(console.error);
  }, [projectId]);

  const handleInvite = async () => {
    try {
      const result = await apiRequest('/api/admin/users/invite', {
        method: 'POST',
        body: {
          email: inviteForm.email,
          project_ids: [projectId],
          role: inviteForm.role,
        },
      });

      if (result) {
        setInviteForm({ email: '', role: 'Collaborator' });
        // Refresh participants
        apiRequest(`/api/projects/${projectId}/participants`).then(setParticipants);
      }
    } catch (error) {
      console.error('Failed to send invitation:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-800 p-6 rounded-lg max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-white">Project Participants</h3>
          <Button variant="ghost" onClick={onClose} className="text-gray-400">×</Button>
        </div>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-300">Current Participants</h4>
            {participants.map((p, i) => (
              <div key={i} className="flex justify-between items-center p-2 bg-gray-700 rounded">
                <span className="text-white">{p.email}</span>
                <span className="text-xs text-gray-400">{p.role}</span>
              </div>
            ))}
          </div>
          
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-300">Invite New Participant</h4>
            <input
              type="email"
              placeholder="Email address"
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600"
            />
            <Select value={inviteForm.role} onValueChange={(role) => setInviteForm({ ...inviteForm, role })}>
              <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Collaborator">Collaborator</SelectItem>
                <SelectItem value="Manager">Manager</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              onClick={handleInvite}
              disabled={!inviteForm.email}
              className="w-full bg-primary hover:bg-cyan-300 text-black"
            >
              Send Invitation
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminProjectsPage() {
  const [, setLocation] = useLocation();
  const [moduleFilter, setModuleFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [showForm, setShowForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProject, setEditingProject] = useState<ProjectData | null>(null);
  const [editProjectPayload, setEditProjectPayload] = useState<ProjectPayload>({} as ProjectPayload);
  const [showParticipantsFor, setShowParticipantsFor] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newProject, setNewProject] = useState({
    name: "",
    client_name: "",
    has_maturity: false,
    has_value: false,
  });

  // Fetch projects data
  const { data: projects, isLoading, error, refetch } = useQuery<ProjectData[]>({
    queryKey: ['/api/admin/projects'],
    refetchInterval: 60000,
    staleTime: 30000
  });

  const { toast } = useToast();

  const sfSyncMutation = useMutation({
    mutationFn: async (projectId: string) => {
      // Use apiRequest to ensure Authorization header and token refresh
      return apiRequest(`/api/admin/projects/${projectId}/sf-sync`, { method: 'POST' });
    },
    onSuccess: (data: any) => {
      toast({ title: 'Salesforce sync completed', description: data?.message || undefined });
    },
    onError: (err: any) => {
      const message = err?.message || 'Salesforce sync failed';
      toast({ title: 'Salesforce sync failed', description: message, variant: 'destructive' });
    },
  });

  const handleCreate = async () => {
    try {
      const res = await apiRequest("/api/projects", {
        method: "POST",
        body: {
          name: newProject.name,
          client_org: newProject.client_name,
          has_maturity: newProject.has_maturity,
          has_value: newProject.has_value,
        },
      });

      if (res) {
        refetch();
        setNewProject({ name: "", client_name: "", has_maturity: false, has_value: false });
        setShowForm(false);
      }
    } catch (error) {
      console.error("Failed to create project:", error);
    }
  };

  const openEditModal = (proj: ProjectData) => {
    setEditingProject(proj);
    const pricingEnabled = (proj as any).value_assessment_pricing_enabled ?? false;
    setEditProjectPayload({
      name: proj.name,
      description: proj.description || '',
      client_org: proj.client_org || '',
      // DO NOT include owner_email - it gets transformed by GET endpoint and shouldn't be editable
      primary_contact_name: (proj as any).primary_contact_name || '',
      primary_contact_email: (proj as any).primary_contact_email || '',
      client_collaborators: (proj as any).client_collaborators || [],
      salesforce_opportunity_id: proj.salesforce_opportunity_id || '',
      nda_status: (proj as any).nda_status || 'not_started',
      nda_reference: (proj as any).nda_agreement_id || '',
      nda_signed_date: (proj as any).nda_signed_at ? new Date((proj as any).nda_signed_at).toISOString().split('T')[0] : '',
      export_options_enabled: (proj as any).export_options_enabled ?? true,
      includePricingInValueAssessment: pricingEnabled,
      show_financial_metrics: pricingEnabled,
      assessment_scheduled_at: (proj as any).assessment_scheduled_at || '',
      assessment_completed_at: (proj as any).assessment_completed_at || '',
      presentation_scheduled_at: (proj as any).presentation_scheduled_at || '',
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    if (!editingProject) return;

    setIsSubmitting(true);
    try {
      const res = await apiRequest(`/api/projects/${editingProject.id}`, {
        method: "PUT",
        body: editProjectPayload,
      });

      if (res) {
        toast({
          title: "Success",
          description: "Project updated successfully",
        });
        refetch();
        setShowEditModal(false);
        setEditingProject(null);
      }
    } catch (error) {
      console.error("Failed to update project:", error);
      toast({
        title: "Error",
        description: "Failed to update project",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (projectId: string, projectName: string) => {
    const confirmed = window.confirm(`Are you sure you want to delete "${projectName}"? This action cannot be undone.`);
    if (!confirmed) return;

    try {
      await apiRequest(`/api/projects/${projectId}`, { method: 'DELETE' });

      // Refresh the projects table
      await refetch();
    } catch (error) {
      console.error('Failed to delete project:', error);
      alert(error instanceof Error ? error.message : 'An error occurred while deleting the project');
    }
  };

  const filtered = projects?.filter((project) => {
    const completion = project.module_completion || project.completion || { maturity: 0, roi: 0, value_engineering: 0 };
    const hasMaturity = completion.maturity > 0;
    const hasValue = completion.value_engineering > 0;
    const hasRoi = completion.roi > 0;
    const isComplete = project.status?.toLowerCase() === 'completed';
    
    const moduleMatch =
      moduleFilter === "All" ||
      (moduleFilter === "Maturity" && hasMaturity) ||
      (moduleFilter === "Value" && (hasValue || hasRoi));
    const statusMatch =
      statusFilter === "All" ||
      (statusFilter === "Completed" && isComplete) ||
      (statusFilter === "Incomplete" && !isComplete);
    return moduleMatch && statusMatch;
  }) || [];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-700 rounded w-1/3"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
            <div className="h-96 bg-gray-800 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold text-red-400 mb-4">Error Loading Projects</h1>
            <p className="text-gray-400">Failed to load project data. Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => setLocation('/admin')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-white">Project Management</h1>
              <p className="text-gray-400 mt-1">Manage all projects in the system</p>
            </div>
          </div>
        </div>

        {/* Enhanced Projects Table */}
        <Card className="bg-gray-800 border-gray-700 shadow-md rounded-xl">
          <CardHeader>
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-primary mb-4 flex items-center">
                <FolderOpen className="mr-2 h-5 w-5" />
                Projects Overview ({filtered.length})
              </h2>
              <Button
                onClick={() => setShowForm(!showForm)}
                className="bg-primary hover:bg-cyan-300 text-black font-medium"
              >
                {showForm ? "Cancel" : "Create Project"}
              </Button>
            </div>

            {showForm && (
              <div className="bg-[#0f2f39] p-4 rounded-lg mt-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Project Name"
                    value={newProject.name}
                    onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                    className="w-full p-2 rounded bg-forestBlack text-white border border-gray-600 focus:border-primary outline-none"
                  />
                  <input
                    type="text"
                    placeholder="Client Name"
                    value={newProject.client_name}
                    onChange={(e) => setNewProject({ ...newProject, client_name: e.target.value })}
                    className="w-full p-2 rounded bg-forestBlack text-white border border-gray-600 focus:border-primary outline-none"
                  />
                </div>
                <div className="flex gap-6 text-white">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newProject.has_maturity}
                      onChange={(e) =>
                        setNewProject({ ...newProject, has_maturity: e.target.checked })
                      }
                      className="w-4 h-4"
                    />
                    Maturity Assessment
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newProject.has_value}
                      onChange={(e) =>
                        setNewProject({ ...newProject, has_value: e.target.checked })
                      }
                      className="w-4 h-4"
                    />
                    Value Engineering
                  </label>
                </div>
                <Button
                  onClick={handleCreate}
                  disabled={!newProject.name || !newProject.client_name}
                  className="bg-green-600 hover:bg-green-500 text-white"
                >
                  Save Project
                </Button>
              </div>
            )}

            <div className="flex gap-4 mt-4">
              <Select value={moduleFilter} onValueChange={setModuleFilter}>
                <SelectTrigger className="bg-forestBlack border-gray-600 text-white min-w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Modules</SelectItem>
                  <SelectItem value="Maturity">Maturity</SelectItem>
                  <SelectItem value="Value">Value</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-forestBlack border-gray-600 text-white min-w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Status</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Incomplete">Incomplete</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="table-auto w-full text-sm text-left text-white">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-700">
                    <th className="px-4 py-2">Project</th>
                    <th className="px-4 py-2">Client</th>
                    <th className="px-4 py-2">Maturity</th>
                    <th className="px-4 py-2">Value</th>
                    <th className="px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((proj) => (
                    <tr key={proj.id} className="border-b border-gray-700 hover:bg-gray-700/50">
                      <td className="px-4 py-2">{proj.name}</td>
                      <td className="px-4 py-2">{proj.client_org || 'N/A'}</td>
                      <td className="px-4 py-2 text-center">
                        <ProjectStatusBadge completed={(proj.module_completion || proj.completion || { maturity: 0 }).maturity > 0} />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <ProjectStatusBadge completed={(proj.module_completion || proj.completion || { value_engineering: 0 }).value_engineering > 0} />
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditModal(proj)}
                            className="text-primary hover:text-cyan-300 hover:bg-cyan-900/20 text-xs px-2 py-1"
                            title="Edit Project"
                          >
                            <PencilLine className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setShowParticipantsFor(proj.id)}
                              className="text-primary hover:text-cyan-300 hover:bg-cyan-900/20 text-xs px-2 py-1"
                              title="Edit Participants"
                            >
                              <Users className="h-4 w-4 mr-1" />
                              Participants
                            </Button>

                            {proj.salesforce_opportunity_id && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => sfSyncMutation.mutate(proj.id)}
                                disabled={sfSyncMutation.isPending}
                                title="Sync assessment to Salesforce"
                              >
                                <RefreshCw className="h-4 w-4 mr-1" />
                                Sync SF
                              </Button>
                            )}

                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(proj.id, proj.name)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20 text-xs px-2 py-1"
                            title="Delete Project"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filtered.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-400">No projects found matching the selected filters.</p>
              </div>
            )}
          </CardContent>
          
          {/* Participants Modal */}
          {showParticipantsFor && (
            <ParticipantsModal
              projectId={showParticipantsFor}
              onClose={() => setShowParticipantsFor(null)}
            />
          )}

          {/* Edit Project Modal */}
          {showEditModal && editingProject && (
            <EditProjectModal
              isOpen={showEditModal}
              onClose={() => {
                setShowEditModal(false);
                setEditingProject(null);
              }}
              onSubmit={handleEditSubmit}
              editProject={editProjectPayload}
              setEditProject={setEditProjectPayload}
              isSubmitting={isSubmitting}
              projectData={editingProject}
            />
          )}
        </Card>
      </div>
    </div>
  );
}
